import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  addVoucherApi,
  deleteVoucherApi,
  getAllVoucherApi
} from '@/api/voucher'
import { sendVoucherMail } from '@/api/new-api/loyalty/voucher'

// Thunks
export const fetchVouchers = createAsyncThunk(
  'voucher/fetchAll',
  async (
    { restaurant_id, page = 1, search = '', status = '', per_page = 10 },
    { rejectWithValue }
  ) => {
    const params = new URLSearchParams()

    params.append('per_page', per_page)
    params.append('page', page)

    if (search) {
      params.append('search_term', search)
    }

    if (status) {
      params.append('status', status)
    }

    const res = await getAllVoucherApi(restaurant_id, params.toString())

    if (res.status === 'success') {
      return { ...res.data, search }
    }

    return rejectWithValue(res.message)
  }
)

export const deleteVoucherById = createAsyncThunk(
  'voucher/delete',
  async (id, { rejectWithValue }) => {
    const res = await deleteVoucherApi(id)
    if (res.status === 'success') return id
    return rejectWithValue(res.message)
  }
)

export const sendVoucherEmail = createAsyncThunk(
  'voucher/sendEmail',
  async ({ id, data, onProgress, signal }, { rejectWithValue }) => {
    const config = {};
    if (signal) config.signal = signal;
    if (onProgress) {
      config.onDownloadProgress = (progressEvent) => {
        const loaded = progressEvent.loaded || 0;
        const total = progressEvent.total || null;
        let percent = null;
        if (total && total > 0) {
          percent = Math.round((loaded * 100) / total);
        }
        onProgress({ loaded, total, percent });
      };
    }
    const res = await sendVoucherMail(id, data, config)
    if (res.status === 'success') return res.data
    return rejectWithValue(res.message)
  }
)

// Initial State
const initialState = {
  vouchers: [],
  singleVoucherCard: null,
  totalPages: 1,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
  search: '',
  status: '',
  perPage: 10,
  currentPage: 1
}

// Slice
const voucherSlice = createSlice({
  name: 'voucher',
  initialState,
  reducers: {
    clearVoucherMessages: state => {
      state.success = null
      state.error = null
      state.requestStatus = false
      state.totalPages = 1
      state.search = ''
    },
    setVoucherSearch: (state, action) => {
      state.search = action.payload
    },
    setStatus: (state, action) => {
      state.status = action.payload
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    }
  },
  extraReducers: builder => {
    builder

      // Fetch All
      .addCase(fetchVouchers.pending, state => {
        state.getLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(fetchVouchers.fulfilled, (state, action) => {
        state.getLoading = false
        state.vouchers = action.payload.vouchers
        state.requestStatus = true
        state.totalPages = action.payload.pagination.last_page
      })
      .addCase(fetchVouchers.rejected, (state, action) => {
        state.getLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
      // Delete
      .addCase(deleteVoucherById.pending, state => {
        state.deleteLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(deleteVoucherById.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.vouchers = state.vouchers.filter(
          voucher => voucher.id !== action.payload
        )
        state.success = 'Voucher deleted successfully!'
        state.requestStatus = true
      })
      .addCase(deleteVoucherById.rejected, (state, action) => {
        state.deleteLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
      // Send Email
      .addCase(sendVoucherEmail.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(sendVoucherEmail.fulfilled, (state, action) => {
        state.updateLoading = false
        state.success = 'Voucher email sent successfully!'
        state.requestStatus = true
      })
      .addCase(sendVoucherEmail.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
  }
})

export const {
  clearVoucherMessages,
  setVoucherSearch,
  setStatus,
  setPerPage,
  setCurrentPage
} = voucherSlice.actions
export default voucherSlice.reducer
